import React, { useEffect, useState } from "react";
import { Box, Chip, Typography, useMediaQuery, useTheme } from "@mui/material";
import MaterialTable from "../../../Components/MaterialTable";
import { IColumn } from "../../../Components/MaterialTable/MaterialTable";
import TableRowMenu from "../../../Components/MaterialTable/TableRowMenu";
import ConfirmModal from "../../../Components/Modals/ConfirmModal";
import { formatDate } from "../../../utils/formatDate";
import {
	AffiliateUser,
	COUPON_STATUS_COLOR,
	COUPON_STATUS_LABEL,
	CouponStatusEnum
} from "../../../store/services/affiliate/dto/affiliate.dto";
import AffiliateUsersCardsList from "./AffiliateUsersCardsList";
import {
	useLazyGetAffiliateUsersQuery,
	useUpdateAffiliateCouponStatusMutation
} from "../../../store/services/admin/admin.service";
import { useAppSelector } from "../../../store";
import { getFullAffiliateState } from "../../../store/slices/affiliate/affiliate.slice";
import { toast } from "react-toastify";
import { notifyError } from "../../../utils/NotifyError";
import { IError } from "../../../utils/getErrorMessage";

const PER_PAGE = 20;

const AffiliateUsers = () => {
	const theme = useTheme();
	const isSmallerScreen = useMediaQuery(theme.breakpoints.down("md"));
	const {
		adminAffiliateUsers,
		adminAffiliateUsersTotalItems,
		isFetchingAdminAffiliateUsers,
		isAdminUpdatingAffiliateCoupon
	} = useAppSelector(getFullAffiliateState);
	const [fetchAffiliateUsers] = useLazyGetAffiliateUsersQuery();
	const [callUpdateAffiliateCouponStatus] = useUpdateAffiliateCouponStatusMutation();
	const [page, setPage] = useState(1);

	const [confirmActivateStatusUser, setConfirmActivateStatusUser] = useState<{
		id: string;
		status: CouponStatusEnum;
		coupon: string;
	} | null>(null);

	const handleConfirmUpdateStatus = () => {
		if (!confirmActivateStatusUser) {
			return null;
		}
		callUpdateAffiliateCouponStatus({
			user_id: confirmActivateStatusUser.id,
			code: confirmActivateStatusUser.coupon,
			status: confirmActivateStatusUser.status
		}).then(({ data, error }) => {
			if (data) {
				toast.success(`Coupon status has been set to ${COUPON_STATUS_LABEL[confirmActivateStatusUser.status]}`, {
					autoClose: 3000
				});
			}
			if (error) {
				notifyError(error as IError);
			}
		});

		setConfirmActivateStatusUser(null);
	};

	useEffect(() => {
		fetchAffiliateUsers({ page: 1, perPage: PER_PAGE });
	}, []);

	const handlePageChange = (newPage: number) => {
		if (newPage > 0) {
			setPage(newPage);
			fetchAffiliateUsers({ page: newPage, perPage: PER_PAGE });
		}
	};

	const columns: IColumn<AffiliateUser>[] = [
		{
			label: "First Name",
			dataKey: "firstName",
			Cell: ({ rowData }) => <Typography fontSize={13}>{rowData.firstName}</Typography>
		},
		{
			label: "Last Name",
			dataKey: "lastName",
			Cell: ({ rowData }) => <Typography fontSize={13}>{rowData.lastName}</Typography>
		},
		{
			label: "Email",
			dataKey: "email",
			Cell: ({ rowData }) => <Typography fontSize={13}>{rowData.email}</Typography>
		},
		{
			label: "Business Name",
			dataKey: "businessName",
			Cell: ({ rowData }) => <Typography fontSize={13}>{rowData.businessName}</Typography>
		},
		{
			label: "Coupon",
			dataKey: "coupon",
			Cell: ({ rowData }) => <Chip label={rowData.coupon} size={"small"} variant={"outlined"} />
		},
		{
			label: "Status",
			dataKey: "status",
			Cell: ({ rowData }) => {
				// const status = getStatus(rowData);
				return (
					<Chip
						label={COUPON_STATUS_LABEL[rowData.status]}
						color={COUPON_STATUS_COLOR[rowData.status]}
						size={"small"}
					/>
				);
			}
		},
		{
			label: "Date Created",
			dataKey: "createdAt",
			Cell: ({ rowData }) => (
				<Typography fontSize={13}>{formatDate(rowData.createdAt, "Africa/Cairo", "PP")}</Typography>
			)
		},
		{
			label: "",
			dataKey: "menu",
			Cell: ({ rowData }) => (
				<TableRowMenu
					items={[
						{
							label: "Activate",
							visible: rowData.status === CouponStatusEnum.pending || rowData.status === CouponStatusEnum.disabled,
							onSelected: () =>
								setConfirmActivateStatusUser({
									id: rowData.userId,
									coupon: rowData.coupon,
									status: CouponStatusEnum.active
								})
						},
						{
							label: "Disable",
							visible: rowData.status === CouponStatusEnum.active,
							onSelected: () =>
								setConfirmActivateStatusUser({
									id: rowData.userId,
									coupon: rowData.coupon,
									status: CouponStatusEnum.disabled
								})
						}
					]}
				/>
			)
		}
	];

	return (
		<Box>
			{isSmallerScreen ? (
				<AffiliateUsersCardsList
					data={adminAffiliateUsers}
					setConfirmActivateStatusUser={setConfirmActivateStatusUser}
					page={page}
					perPage={PER_PAGE}
					totalItems={adminAffiliateUsersTotalItems}
					onPageChange={handlePageChange}
				/>
			) : (
				<MaterialTable
					tableLabel={"Affiliate Users"}
					columns={columns}
					data={adminAffiliateUsers}
					isLoading={isFetchingAdminAffiliateUsers}
					page={page}
					rowsPerPage={PER_PAGE}
					totalItems={adminAffiliateUsersTotalItems}
					onPageChange={handlePageChange}
				/>
			)}
			<ConfirmModal
				open={!!confirmActivateStatusUser}
				title={`${confirmActivateStatusUser?.status === CouponStatusEnum.active ? "Activate Coupon" : "Disable Coupon"}`}
				message={`Are you sure you want to ${confirmActivateStatusUser?.status === CouponStatusEnum.active ? "activate" : "disable"} this affiliate coupon?`}
				confirmText={`${confirmActivateStatusUser?.status === CouponStatusEnum.active ? "Activate" : "Disable"}`}
				cancelText={"Cancel"}
				loading={isAdminUpdatingAffiliateCoupon}
				onClose={() => setConfirmActivateStatusUser(null)}
				onConfirm={handleConfirmUpdateStatus}
			/>
		</Box>
	);
};

export default AffiliateUsers;
